import { Checkbox, Typography } from '@remarkable/ark-web';
import { PaymentElement } from '@stripe/react-stripe-js';
import { useForm } from 'react-hook-form';

import { ButtonClicked } from 'src/ampli';
import { tracker } from 'src/analytics/tracker';
import { ComponentLocations } from 'src/analytics/trackingTypes';
import type { useCart } from 'src/api/queries';
import { Button, Form, NotificationBox } from 'src/components';
import { createContentSwitch } from 'src/utils/createContentSwitch';
import {
  CustomStripeError,
  StripeIntentType,
  useStripe,
} from 'src/utils/stripe';
import { SUPPORT_URL } from 'src/utils/urls/supportUrls';

import { Trans, useLingui } from '@lingui/react/macro';
import { LinkExternal } from 'src/components/LinkExternal';
import { useCheckoutForm } from '../checkoutFormStore';
import type { CheckoutFormState } from './Checkout.types';
import { Price } from './Price';
import { useCheckout } from './useCheckout';

const TermsAndConditionsLink = () => (
  <LinkExternal
    to={SUPPORT_URL.LEGAL_OVERVIEW}
    inline
    componentLocation={ComponentLocations.CHECKOUT.CONNECT_CHECKOUT}
  >
    <Trans>Terms and Conditions</Trans>
  </LinkExternal>
);

export function CheckoutPaymentForm({
  cart,
}: {
  cart: ReturnType<typeof useCart>;
}) {
  const { t } = useLingui();
  const stripeIntentType =
    cart.checkoutType === 'store'
      ? StripeIntentType.Payment
      : StripeIntentType.Setup;
  const { paymentElementProps, isPaymentElementReady } =
    useStripe(stripeIntentType);
  const [checkoutFormState, setCheckoutFormState] = useCheckoutForm();
  const form = useForm<Partial<CheckoutFormState>>({
    defaultValues: { ...checkoutFormState },
  });
  const checkout = useCheckout(cart);

  const hasAcceptedTerms = form.watch('hasAcceptedTerms');
  const showOnCheckoutType = createContentSwitch(cart.checkoutType);
  return (
    // browser translation breaks the checkout form
    <div translate="no">
      <Form
        {...form}
        onSubmit={(data) => {
          const newState = {
            ...checkoutFormState,
            ...data,
          };
          tracker.trackEvent(
            new ButtonClicked({
              component_location: ComponentLocations.CHECKOUT.CONNECT_CHECKOUT,
              text: `Submit payment form`,
              action: `submit payment form`,
            })
          );
          setCheckoutFormState(newState);
          checkout.mutate(newState);
        }}
      >
        <div className="flex flex-col gap-24">
          <PaymentElement {...paymentElementProps} />
          <div className="flex items-start gap-8">
            <Checkbox
              data-cy="accept-terms-and-conditions"
              id="terms"
              name="hasAcceptedTerms"
              required
              onClick={() => {
                tracker.trackEvent(
                  new ButtonClicked({
                    component_location:
                      ComponentLocations.CHECKOUT.CONNECT_CHECKOUT,
                    text: `Accept terms and conditions checkbox`,
                    action: `toggle accept TOC checkbox ${
                      hasAcceptedTerms ? 'off' : 'on'
                    }`,
                  })
                );
              }}
            ></Checkbox>
            <Typography variant="body-sm" className="my-0">
              {showOnCheckoutType({
                ['store']: (
                  <Trans context="Terms and conditions for Connect">
                    By subscribing you accept the <TermsAndConditionsLink /> for
                    Connect and agree to a payment of <Price cart={cart} />
                    &nbsp;Cancel anytime.
                  </Trans>
                ),
                ['connectOffer']: (
                  <Trans context="Terms and conditions for Connect with free trial">
                    I accept the <TermsAndConditionsLink /> for Connect and
                    agree to a payment of <Price cart={cart} /> once the free
                    subscription period ends. Cancel anytime.
                  </Trans>
                ),
                ['winbackOffer']: (
                  <Trans context="Terms and conditions for Connect with free trial">
                    I accept the <TermsAndConditionsLink /> for Connect and
                    agree to a payment of <Price cart={cart} /> once the free
                    subscription period ends. Cancel anytime.
                  </Trans>
                ),
              })}
            </Typography>
          </div>
          {checkout.isError &&
            (checkout.error instanceof CustomStripeError ? (
              <NotificationBox
                data-cy="checkout-error-box"
                error={checkout.error}
                title={
                  checkout.error.data.type === 'validation_error'
                    ? t`Please check your information`
                    : t`Something went wrong during checkout`
                }
                className="my-16"
              >
                {checkout.error.data.type === 'validation_error' ||
                checkout.error.data.type === 'card_error'
                  ? checkout.error.data.message
                  : t`Please check your information and try again. If the problem persists, please contact support.`}
              </NotificationBox>
            ) : (
              <NotificationBox
                data-cy="checkout-error-box"
                variant="error"
                title={t`Something went wrong during checkout`}
                className="my-16"
                error={checkout.error}
              >
                <Trans>
                  Please check your information and try again. If the problem
                  persists, please contact support.
                </Trans>
              </NotificationBox>
            ))}
          <Button
            type="submit"
            data-cy="activate-connect-button"
            variant="primary"
            className="w-full ll:w-fit"
            disabled={!isPaymentElementReady}
            loading={form.formState.isSubmitting || checkout.isPending}
            analytics={{
              location: ComponentLocations.CHECKOUT.CONNECT_CHECKOUT,
              text: showOnCheckoutType({
                ['connectOffer']: t`Activate free trial`,
                ['winbackOffer']: t`Activate free trial`,
                ['store']: t`Subscribe`,
              }) as string,
              action: 'submit payment information',
            }}
          >
            {showOnCheckoutType({
              ['connectOffer']: t`Activate free trial`,
              ['winbackOffer']: t`Activate free trial`,
              ['store']: t`Subscribe`,
            })}{' '}
          </Button>
        </div>
      </Form>
    </div>
  );
}
